/**
 * @see https://umijs.org/docs/max/access#access
 * */

/**
 * 检查用户是否有访问指定菜单的权限
 */
function hasMenuAccess(currentUser: API.CurrentUser | undefined, menuPath: string): boolean {
  if (!currentUser) {
    return false;
  }

  if (currentUser.access === 'admin' || currentUser.role === 'admin') {
    return true;
  }

  const menus = currentUser.menus || [];
  
  if (menus.includes('*')) {
    return true;
  }

  if (menus.includes(menuPath)) {
    return true;
  }

  // 父→子：拥有父菜单权限则可访问子菜单（如 /showcase → /showcase/list）
  const parentMenu = '/' + menuPath.split('/')[1];
  if (menuPath !== parentMenu && menus.includes(parentMenu)) {
    return true;
  }

  // 子→父：拥有任意子菜单权限则父菜单应可见（如 /doc-management/groups → /doc-management）
  if (menus.some(m => m.startsWith(menuPath + '/'))) {
    return true;
  }

  return false;
}

export default function access(initialState: { currentUser?: API.CurrentUser } | undefined) {
  const { currentUser } = initialState ?? {};
  
  return {
    // 管理员权限
    canAdmin: currentUser && (currentUser.access === 'admin' || currentUser.role === 'admin'),
    
    // 搜索权限
    canAccessSearch: hasMenuAccess(currentUser, '/search'),

    // 文档管理权限（父菜单可见性由子→父逻辑自动推导）
    canManageDoc: hasMenuAccess(currentUser, '/doc-management'),
    canAccessDocGroups: hasMenuAccess(currentUser, '/doc-management/groups'),
    canAccessStandaloneDocs: hasMenuAccess(currentUser, '/doc-management/standalone-docs'),
    canAccessKnowledgeBase: hasMenuAccess(currentUser, '/doc-management/knowledge-base'),
    canAccessDocTrash: hasMenuAccess(currentUser, '/doc-management/trash'),
    canAccessApiKeys: hasMenuAccess(currentUser, '/doc-management/api-keys'),
    
    // 案例管理权限
    canAccessShowcase: hasMenuAccess(currentUser, '/showcase'),
    canCreateShowcase: hasMenuAccess(currentUser, '/showcase/create'),
    
    // 分类管理权限
    canAccessCategory: hasMenuAccess(currentUser, '/showcase/category') || hasMenuAccess(currentUser, '/category'),
    
    // 客户分享管理权限
    canAccessCustomerShare: hasMenuAccess(currentUser, '/showcase/customer-share') || hasMenuAccess(currentUser, '/customer-share'),
    canCreateCustomerShare: hasMenuAccess(currentUser, '/showcase/customer-share/create') || hasMenuAccess(currentUser, '/customer-share/create'),
    
    // 团队权限
    canAccessTeam: hasMenuAccess(currentUser, '/team'),
    
    // 系统设置权限
    canAccessSettings: hasMenuAccess(currentUser, '/settings'),
    
    // 用户管理权限
    canAccessUserManagement: hasMenuAccess(currentUser, '/admin/users'),
    
    // 角色管理权限
    canAccessRoleManagement: hasMenuAccess(currentUser, '/admin/roles'),
    
    // 菜单管理权限
    canAccessMenuManagement: hasMenuAccess(currentUser, '/admin/menus'),
    
    // App 版本管理权限
    canAccessAppVersionManagement: hasMenuAccess(currentUser, '/admin/app-versions'),

    // 数据权限管理
    canAccessDataPermission: hasMenuAccess(currentUser, '/admin/data-permissions'),

    // 访问统计
    canAccessSystemStats: hasMenuAccess(currentUser, '/admin/system-stats'),

    // Agent 智能模块权限
    canAccessAgent: hasMenuAccess(currentUser, '/agent'),
    canAccessAgentHealth: hasMenuAccess(currentUser, '/agent/health'),
    canAccessAgentSuggestions: hasMenuAccess(currentUser, '/agent/suggestions'),
    canAccessAgentConfig: hasMenuAccess(currentUser, '/agent/config'),
    canAccessAgentAudit: hasMenuAccess(currentUser, '/agent/audit'),
    canAccessAgentTokenUsage: hasMenuAccess(currentUser, '/agent/token-usage'),
    canAccessAgentMemory: hasMenuAccess(currentUser, '/agent/memory'),
    canAccessAgentConversationLogs: hasMenuAccess(currentUser, '/agent/conversation-logs'),
  };
}
